import { account_copy } from "../../../yap-frontend-rs/pkg";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ModeToggle } from "@/browse/mode-toggle";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NotificationSettings } from "@/browse/notification-settings";
import { PasskeySettings } from "@/browse/passkey-settings";
import { LogOut, AlertTriangle, ArrowLeft, User } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { SyncStatusDialog } from "@/browse/sync-status-dialog";
import type { UserInfo } from "@/app/context";
import { useAuthDialog } from "@/auth/auth-dialog-provider";

interface HeaderProps {
  userInfo: UserInfo | undefined;
  onSignOut: () => void;
  showSignupNag?: boolean;
  backButton?: {
    label: string;
    onBack: () => void;
  };
  title?: string;
  dailyGoalPercent?: number;
}

export function Header({
  userInfo,
  onSignOut,
  showSignupNag = false,
  backButton,
  title = "Yap.Town",
  dailyGoalPercent,
}: HeaderProps) {
  const { openSignIn, openSignUp } = useAuthDialog();
  const navigate = useNavigate();
  const copy = account_copy();

  return (
    <div className="space-y-2">
      {dailyGoalPercent !== undefined && (
        <Progress
          value={Math.min(dailyGoalPercent, 100)}
          className="h-1 rounded-none fixed top-0 left-0 right-0 z-50 bg-transparent"
        />
      )}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            {backButton && (
              <Button
                variant="ghost"
                size="icon"
                onClick={backButton.onBack}
                className="h-8 w-10"
                title={backButton.label}
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            )}
            <h1 className="text-2xl font-bold drop-shadow-[0_0px_8px_rgba(255,255,255,0.8)] dark:drop-shadow-[0_0px_8px_rgba(0,0,0,1)]">
              <Link to="/about">
                <span className="hidden sm:inline">{title}</span>
              </Link>
              <span className="sm:hidden">{title.split(".")[0]}</span>
            </h1>
          </div>
          {!backButton && userInfo && (
            <div className="animate-fade-in-delayed">
              <SyncStatusDialog />
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {userInfo ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="text-sm text-muted-foreground hover:text-foreground animate-fade-in-delayed gap-2"
                >
                  {userInfo.displayName || userInfo.email}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <NotificationSettings />
                <PasskeySettings />
                <DropdownMenuItem
                  onClick={() => navigate(`/user/id/${userInfo.id}`)}
                >
                  <User className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onSignOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="ghost" size="sm" onClick={openSignIn}>
              {copy.sign_in_action}
            </Button>
          )}
          <ModeToggle />
        </div>
      </div>

      {!userInfo && showSignupNag && (
        <Card
          variant="light"
          className="p-3 flex-row items-center gap-3 mb-2 py-3"
        >
          <AlertTriangle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium">{copy.prompt_title}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {copy.prompt_body}
            </p>
          </div>
          <Button
            onClick={openSignUp}
            variant="outline"
            size="sm"
            className="flex-shrink-0"
          >
            {copy.prompt_action}
          </Button>
        </Card>
      )}
    </div>
  );
}
